import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { LiveSessionService } from '@/services/liveSession.service';
import type { LiveSession, UpdateLiveSessionData } from '@/types';
import { useFirestoreSubscription } from './useFirestoreSubscription';

interface UseLiveSessionResult {
    session: LiveSession | null;
    isLoading: boolean;
    error: Error | null;
    createSession: (playlistId: string, playlistName: string, totalTracks: number) => Promise<string>;
    joinSession: (sessionId: string) => Promise<void>;
    leaveSession: (sessionId: string) => Promise<void>;
    updateState: (updates: UpdateLiveSessionData) => Promise<void>;
    endSession: () => Promise<void>;
    isAdmin: boolean;
}

export function useLiveSession(sessionId?: string): UseLiveSessionResult {
    const { user } = useAuth();

    // Subscribe to session if ID is provided
    const { data: session, isLoading, error } = useFirestoreSubscription<LiveSession | null>(
        (callback) => {
            if (!sessionId) {
                callback(null);
                return () => { };
            }
            return LiveSessionService.subscribeToSession(sessionId, callback);
        },
        [sessionId]
    );

    const isAdmin = !!(user && session && user.id === session.adminId);

    const createSession = useCallback(async (playlistId: string, playlistName: string, totalTracks: number) => {
        if (!user) throw new Error('Must be logged in');
        return LiveSessionService.startSession(playlistId, user.id, playlistName, totalTracks);
    }, [user]);

    const joinSession = useCallback(async (sid: string) => {
        if (!user) return;
        await LiveSessionService.joinSession(sid, user.id);
    }, [user]);

    const leaveSession = useCallback(async (sid: string) => {
        if (!user) return;
        await LiveSessionService.leaveSession(sid, user.id);
    }, [user]);

    const updateState = useCallback(async (updates: UpdateLiveSessionData) => {
        if (!sessionId) return;
        // Ideally checking isAdmin here too, but Firestore rules enforce it ultimately
        await LiveSessionService.updateSessionState(sessionId, updates);
    }, [sessionId]);

    const endSession = useCallback(async () => {
        if (!sessionId) return;
        await LiveSessionService.endSession(sessionId);
    }, [sessionId]);

    return {
        session,
        isLoading,
        error,
        createSession,
        joinSession,
        leaveSession,
        updateState,
        endSession,
        isAdmin
    };
}
